import React, { useCallback, useEffect, useRef, useState } from "react";
import Calendar from "react-calendar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { addDays, format, isSameDay, isSameMonth, isWithinInterval, parse, startOfMonth } from "date-fns";
import AnchoredPopover from "@frontend/Financial/TimesheetDayLocks/AnchoredPopover";

interface WeekPickerCalendarProps {
  anchor: HTMLElement | null;
  open: boolean;
  /** Outside click: close only, focus stays where the user clicked. */
  onClose: () => void;
  /** Escape: close and hand focus back to the Today button. */
  onEscape: () => void;
  /** The Calgary calendar date (`calgaryCalendarDate()`), the one "today" this picker knows. */
  today: Date;
  /** Monday of the week the grid is showing. */
  shownWeekStart: Date;
  onPickDate: (date: Date) => void;
}

// The tile's accessible name; also how a focused tile is mapped back to its date.
const LONG_DATE = "EEEE, MMMM d, yyyy";
const ARROW_STEP: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };

/**
 * The month calendar under Today (plans/todo/timesheet-refined-grid.md, F7). Monday first; paging
 * months never moves the grid; clicking a day hands that date to `onPickDate`, which jumps the
 * grid to its Monday–Sunday week. `AnchoredPopover` never moves focus, so this does it: on open
 * the shown week's Monday (or the month's 1st) takes focus and the arrow keys walk the days.
 */
const WeekPickerCalendar: React.FC<WeekPickerCalendarProps> = ({
  anchor,
  open,
  onClose,
  onEscape,
  today,
  shownWeekStart,
  onPickDate,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<Date>(() => startOfMonth(today));
  const [focusDate, setFocusDate] = useState<Date | null>(null);
  // Focus moves only when asked (on open, or by an arrow key) — never because the month changed, so
  // Enter on "Next month" keeps focus on that button instead of landing on a day it would then pick.
  const focusPendingRef = useRef(false);
  const todayKey = format(today, "yyyy-MM-dd");

  // Every open starts on today's month, focused on the shown week's Monday when it is in that month.
  useEffect(() => {
    if (!open) return;
    const month = startOfMonth(today);
    setActive(month);
    setFocusDate(isSameMonth(shownWeekStart, month) ? shownWeekStart : month);
    focusPendingRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when the picker opens
  }, [open]);

  useEffect(() => {
    if (!open || !focusDate || !focusPendingRef.current) return;
    const label = format(focusDate, LONG_DATE);
    const tile = wrapperRef.current
      ?.querySelector(`abbr[aria-label="${label}"]`)
      ?.closest("button");
    if (tile) {
      tile.focus();
      focusPendingRef.current = false;
    }
  }, [open, focusDate, active]);

  // Escape hands focus back to Today wherever focus is (the popover's own listener closes it too).
  useEffect(() => {
    if (!open) return undefined;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onEscape();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onEscape]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const step = ARROW_STEP[e.key];
      if (step === undefined) return;
      const label = (e.target as HTMLElement).querySelector?.("abbr")?.getAttribute("aria-label");
      if (!label || !(e.target as HTMLElement).classList.contains("react-calendar__month-view__days__day")) return;
      const from = parse(label, LONG_DATE, new Date());
      if (Number.isNaN(from.getTime())) return;
      e.preventDefault();
      const next = addDays(from, step);
      if (!isSameMonth(next, active)) setActive(startOfMonth(next));
      focusPendingRef.current = true;
      setFocusDate(next);
    },
    [active],
  );

  const shownWeek = { start: shownWeekStart, end: addDays(shownWeekStart, 6) };

  return (
    <AnchoredPopover anchor={anchor} open={open} onClose={onClose} width={300} ariaLabel="Choose a week" className="ts-weekpicker-popover">
      <div className="ts-weekpicker" ref={wrapperRef} onKeyDown={handleKeyDown}>
        <Calendar
          calendarType="iso8601"
          minDetail="year"
          activeStartDate={active}
          onActiveStartDateChange={({ activeStartDate }) => {
            if (activeStartDate) setActive(activeStartDate);
          }}
          onClickDay={(date) => onPickDate(date)}
          tileClassName={({ date, view }) => {
            if (view !== "month") return null;
            const classes: string[] = [];
            if (isWithinInterval(date, shownWeek)) classes.push("is-shown-week");
            if (format(date, "yyyy-MM-dd") === todayKey) classes.push("is-today");
            return classes;
          }}
          tileContent={({ date, view }) =>
            view === "month" && isSameDay(date, today) ? <span className="ts-sr-only">(today)</span> : null
          }
          prevLabel={<FontAwesomeIcon icon={faChevronLeft} />}
          nextLabel={<FontAwesomeIcon icon={faChevronRight} />}
          prevAriaLabel="Previous month"
          nextAriaLabel="Next month"
          navigationAriaLabel="Choose month and year"
          prev2Label={null}
          next2Label={null}
          formatMonthYear={(_locale, date) => format(date, "MMMM yyyy")}
          formatMonth={(_locale, date) => format(date, "MMM")}
          formatShortWeekday={(_locale, date) => format(date, "EEEEEE")}
          formatLongDate={(_locale, date) => format(date, LONG_DATE)}
        />
      </div>
    </AnchoredPopover>
  );
};

export default WeekPickerCalendar;
